import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMe } from "@/components/Layout";
import { useApi, errMsg } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";

const INVITE_ROLES = ["collaboratore", "visualizzatore", "admin"];

const InviteForm = ({ onDone }) => {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("collaboratore");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/users/invite", { email, role });
      if (r.data.status !== "updated" && !r.data.emailed) {
        toast.warning(`Utente abilitato, ma la mail non è partita${r.data.mail_error ? `: ${r.data.mail_error}` : ""}. Potrà comunque registrarsi con questa email.`);
      } else {
        toast.success(r.data.status === "updated" ? "Utente gi\u00e0 registrato: ruolo aggiornato" : "Invito inviato via email");
      }
      setEmail(""); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="invite-form">
      <UserPlus className="h-5 w-5 text-amber-400 hidden sm:block" />
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@esempio.it" className="flex-1" data-testid="invite-email-input" />
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger className="sm:w-60" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
        <SelectContent>{INVITE_ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`invite-role-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
      </Select>
      <Button type="submit" disabled={busy} data-testid="invite-submit-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}Aggiungi utente</Button>
    </form>
  );
};

export default function UsersPage() {
  const me = useMe();
  const api = useApi();
  const [d, setD] = useState(null);
  const load = () => api.get("/users").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (me.role === "admin") load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (me.role !== "admin") return <Navigate to="/" replace />;
  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.user_id}/role`, { role }); toast.success("Ruolo aggiornato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (inv) => {
    try { await api.delete(`/users/invites/${encodeURIComponent(inv.email)}`); toast.success("Invito annullato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6 rise max-w-4xl" data-testid="users-page">
      <div>
        <p className="micro-label">Accessi</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Utenti</h1>
        <p className="text-slate-400 mt-2 text-sm">Aggiungi un utente con la sua email e scegli il ruolo: riceve un invito e, quando entra, ha gi&agrave; il ruolo giusto. "Solo report" vede soltanto la pagina Report, senza poter modificare nulla.</p>
      </div>
      <InviteForm onDone={load} />
      {d?.invites?.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800" data-testid="invites-list">
          <p className="micro-label px-4 pt-3 pb-2">Inviti in attesa</p>
          {d.invites.map((inv) => (
            <div key={inv.email} className="flex items-center gap-3 p-4" data-testid={`invite-row-${inv.email}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{inv.email}</div>
                <div className="text-xs text-slate-400">{ROLE_LABELS[inv.role]} · {inv.emailed ? "invito inviato via email" : "pu\u00f2 registrarsi dalla pagina di accesso"}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => revoke(inv)} title="Annulla invito" data-testid={`invite-revoke-${inv.email}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
        {(d?.users || []).map((u) => (
          <div key={u.user_id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`user-row-${u.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{u.name || u.email}</div>
              <div className="text-xs text-slate-400 truncate">{u.email}{u.user_id === me.user_id && " · tu"}</div>
            </div>
            {u.is_owner ? (
              <span className="sm:w-64 text-sm font-semibold text-amber-300 px-3 py-2 rounded-md border border-amber-400/40 bg-amber-400/10" data-testid={`user-owner-badge-${u.user_id}`}>Amministratore Plus</span>
            ) : (
              <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                <SelectTrigger className={`sm:w-64 ${u.role === "in_attesa" ? "border-amber-500/60 text-amber-300" : ""}`} data-testid={`user-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{d.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
